(() => {
  const script = document.currentScript;
  const prefix = 'bcd-browse-v3:' + (script?.dataset.theme || 'store') + ':';
  const snapshotKey = prefix + 'snapshot';
  const sourceKey = prefix + 'source';
  const pendingKey = prefix + 'pending';
  const states = new WeakMap();
  let active = null;
  let stopRestoring = () => {};
  const read = key => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; } };
  const write = (key, value) => { try { sessionStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; } };
  const keyFor = value => {
    const url = new URL(value, location.href);
    const query = new URLSearchParams();
    ['gender', 'sort_by', 'page'].forEach(name => { if (url.searchParams.has(name)) query.set(name, url.searchParams.get(name)); });
    return url.origin + url.pathname + (query.size ? '?' + query : '');
  };
  const fresh = state => state && Date.now() - state.time < 2 * 60 * 60 * 1000;
  const navigationBack = () => performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
  const productReferrer = () => { try { const url = new URL(document.referrer); return url.origin === location.origin && /\/products\//.test(url.pathname); } catch { return false; } };
  const headerHeight = () => document.querySelector('.store-header-section')?.getBoundingClientRect().height || 0;
  function anchorFor(catalogue, preferred) {
    const cards = [...catalogue.querySelectorAll('[data-product-id]')];
    const card = preferred || cards.find(item => !item.hidden && item.getBoundingClientRect().bottom > headerHeight());
    return card ? { id: card.dataset.productId, top: card.getBoundingClientRect().top } : null;
  }
  function collect(catalogue, preferred) {
    const grid = catalogue.querySelector('[data-product-grid]');
    const state = states.get(catalogue);
    return {
      key: keyFor(location.href), url: location.href, time: Date.now(),
      y: window.scrollY, anchor: anchorFor(catalogue, preferred),
      gender: catalogue.dataset.genderFilter || 'all',
      next: catalogue.querySelector('[data-auto-load]')?.dataset.next || '',
      pages: state?.pages || [],
      photos: [...catalogue.querySelectorAll('[data-product-id]')].map(card => [card.dataset.productId, card.querySelector('.slides')?.scrollLeft || 0]),
      html: grid.innerHTML
    };
  }
  function persist(catalogue, preferred, leaving = false) {
    const state = states.get(catalogue);
    if (!state || state.restoring || state.leaving) return;
    const data = collect(catalogue, preferred);
    state.saved = data;
    if (leaving) state.leaving = true;
    const { html, ...metadata } = data;
    write(sourceKey, metadata);
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('qqq-browse-v1:')) sessionStorage.removeItem(key);
      }
    } catch {}
    if (!write(snapshotKey, data)) {
      try { sessionStorage.removeItem(snapshotKey); } catch {}
      write(snapshotKey, { ...metadata, html: null });
    }
    try { history.replaceState({ ...history.state, bcdBrowse: metadata }, ''); } catch {}
    if (leaving) write(pendingKey, { key: data.key, time: data.time });
  }
  function align(catalogue, data) {
    stopRestoring();
    const state = states.get(catalogue);
    if (!state || !data) return;
    state.restoring = true;
    state.leaving = false;
    let stopped = false, frame = 0, timer = 0;
    const root = document.documentElement;
    const oldBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    const stop = () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(frame); clearTimeout(timer);
      root.style.scrollBehavior = oldBehavior;
      state.restoring = false;
      ['touchstart', 'pointerdown', 'wheel', 'keydown'].forEach(type => window.removeEventListener(type, stop, true));
      observer?.disconnect();
    };
    const apply = () => {
      if (stopped || !catalogue.isConnected || document.hidden) return;
      const card = [...catalogue.querySelectorAll('[data-product-id]')].find(item => item.dataset.productId === data.anchor?.id && !item.hidden);
      const target = card ? window.scrollY + card.getBoundingClientRect().top - data.anchor.top : data.y;
      window.scrollTo({ top: Math.max(0, target), left: 0, behavior: 'instant' });
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
    observer?.observe(catalogue);
    ['touchstart', 'pointerdown', 'wheel', 'keydown'].forEach(type => window.addEventListener(type, stop, { capture: true, passive: true }));
    stopRestoring = stop;
    const start = performance.now();
    const tick = () => {
      if (stopped) return;
      apply();
      if (performance.now() - start < 1800) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    timer = setTimeout(stop, 2200);
    document.fonts?.ready.then(apply);
  }
  async function restore(catalogue) {
    const key = keyFor(location.href);
    const pending = read(pendingKey);
    const cached = read(snapshotKey);
    const metadata = history.state?.bcdBrowse || read(sourceKey);
    const eligible = navigationBack() || productReferrer() || (fresh(pending) && pending.key === key);
    const data = fresh(cached) && cached.key === key ? cached : metadata;
    const state = { saved: null, pages: [], restoring: false, leaving: false };
    states.set(catalogue, state);
    active = catalogue;
    if (!eligible || !fresh(data) || data.key !== key) return false;
    state.restoring = true;
    const grid = catalogue.querySelector('[data-product-grid]');
    if (data.html) {
      grid.innerHTML = data.html;
    } else {
      const urls = data.pages?.length ? data.pages : [catalogue.dataset.bestSellersUrl || data.url];
      const fragment = document.createDocumentFragment();
      const seen = new Set();
      try {
        for (const value of urls) {
          const url = new URL(value, location.origin);
          if (url.origin !== location.origin) throw new Error('Invalid catalogue origin');
          url.searchParams.set('section_id', catalogue.dataset.sectionId);
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Catalogue restore failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const cards = [...doc.querySelectorAll('[data-product-grid] > [data-product-id]')];
          if (!cards.length) throw new Error('Catalogue restore missing');
          cards.forEach(card => { if (!seen.has(card.dataset.productId)) { seen.add(card.dataset.productId); fragment.append(card); } });
        }
        grid.replaceChildren(fragment);
      } catch { state.restoring = false; return false; }
    }
    catalogue.dataset.genderFilter = data.gender;
    catalogue.querySelector('[data-auto-load]').dataset.next = data.next;
    state.pages = data.pages || [];
    state.saved = data;
    state.restoreData = data;
    try { sessionStorage.removeItem(pendingKey); } catch {}
    return true;
  }
  function bind(catalogue) {
    const state = states.get(catalogue);
    if (!state || state.bound) return;
    state.bound = true;
    if (!state.pages.length) state.pages = [catalogue.dataset.bestSellersUrl || location.href];
    catalogue.addEventListener('click', event => {
      const link = event.target.closest('a[href]');
      if (event.defaultPrevented || !link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
      const url = new URL(link.href, location.href);
      if (url.origin === location.origin && /\/products\//.test(url.pathname)) {
        stopRestoring();
        persist(catalogue, link.closest('[data-product-id]'), true);
      }
    });
    if (state.restoreData) {
      for (const [id, left] of state.restoreData.photos || []) {
        const card = [...catalogue.querySelectorAll('[data-product-id]')].find(item => item.dataset.productId === id);
        const track = card?.querySelector('.slides');
        if (track) track.scrollLeft = left;
      }
      align(catalogue, state.restoreData);
    }
  }
  function loaded(catalogue, url) {
    const state = states.get(catalogue);
    if (state && !state.pages.includes(url)) state.pages.push(url);
  }
  function setReturnLinks() {
    const source = read(sourceKey);
    if (!fresh(source)) return;
    let url;
    try { url = new URL(source.url); } catch { return; }
    if (url.origin !== location.origin || /\/products\//.test(url.pathname)) return;
    document.querySelectorAll('[data-home-dock] a').forEach(link => {
      link.href = url.href;
      link.addEventListener('click', () => write(pendingKey, { key: source.key, time: Date.now() }));
    });
  }
  window.BcdBrowse = { restore, bind, loaded };
  window.addEventListener('pagehide', () => { if (active) persist(active); });
  document.addEventListener('visibilitychange', () => { if (document.hidden && active) persist(active); });
  window.addEventListener('pageshow', event => {
    if (!event.persisted || !active) return;
    const state = states.get(active);
    if (state?.saved) align(active, state.saved);
  });
  window.addEventListener('load', () => { const state = active && states.get(active); if (state?.restoreData && state.restoring) align(active, state.restoreData); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setReturnLinks, { once: true });
  else setReturnLinks();
})();
